// Asignar una observación de la lista de facility a un contratista (#0431,
// ADR-0134). Antes delegaba (POST /delegate, ahora 410); hoy es el hilo:
// el cuerpo es `ProviderAssignForm` —elegir o dar de alta, confirmar el
// destinatario— y la asignación emite el enlace `/r/b/<token>` del
// contratista.
//
// Al terminar: si el servidor confirmó el correo al contratista, el drawer se
// cierra y la lista muestra el toast; si no, queda una pantalla con el
// enlace para copiar, porque el token crudo sólo viaja en esta respuesta.

import { useState } from "react";
import type { FacilityDashboardEntry } from "@bitacorapro/api-types";
import { CopyLinkField } from "./CopyLinkField";
import { Drawer } from "./Drawer";
import { ProviderAssignForm } from "./ProviderAssignForm";
import { useRecipe } from "../lib/api/queries";
import type { AssignResult } from "../lib/postventa/useAssignFlow";
import { threadWords } from "../lib/postventa/words";
import { observationHeadline, sevLabel, sevPillClass } from "../lib/util";

export function AssignDrawer({
  obs,
  storeName,
  notifiesAssetParty,
  onClose,
  onSent,
}: {
  obs: FacilityDashboardEntry;
  storeName: string;
  /** El estado de destino avisa al jefe de tienda (receta). */
  notifiesAssetParty: boolean;
  onClose: () => void;
  /** Se dispara justo antes de cerrar con éxito: la lista muestra su
   *  propio toast (uno dentro del drawer ya no se percibiría, WCAG 4.1.3). */
  onSent?: (message: string) => void;
}) {
  const { data: recipe } = useRecipe();
  const words = threadWords(recipe);
  const [emitted, setEmitted] = useState<AssignResult | null>(null);
  const headline = observationHeadline(obs);
  const reasignar = obs.providerId !== null;

  function onAssigned(r: AssignResult) {
    const verbo = r.revoked || reasignar ? "reasignada" : "asignada";
    const creado = r.providerCreated ? ` · ${words.assigneeLower} agregado a la nómina` : "";
    if (r.notified) {
      onSent?.(`Observación ${verbo} a ${r.quien} · enlace enviado${creado}`);
      onClose();
      return;
    }
    setEmitted(r);
  }

  return (
    <Drawer
      title={
        emitted
          ? "Observación asignada"
          : reasignar
            ? `Reasignar ${words.assigneeLower}`
            : `Asignar ${words.assigneeLower}`
      }
      onClose={onClose}
      lead={
        <>
          <p className="drawer__lead-title">{headline.text}</p>
          <span className="drawer__lead-chips">
            <span>{storeName}</span>
            <span aria-hidden="true">·</span>
            <span>{obs.areaName}</span>
            <span className={`st-pill ${sevPillClass(obs.severity)}`}>
              {sevLabel(obs.severity)}
            </span>
          </span>
        </>
      }
      footer={
        emitted ? (
          <button type="button" className="btn btn--primary" onClick={onClose}>
            Listo
          </button>
        ) : null
      }
    >
      {emitted ? (
        <>
          <p className="cell-sub">
            No se envió correo: copia el enlace y compártelo con {emitted.quien}. No necesita
            cuenta: verá sólo lo que le toca ejecutar en esta {words.assetLower}.
          </p>
          <CopyLinkField
            id={`assign-url-${obs.id}`}
            label={`Enlace del ${words.assigneeLower}`}
            value={emitted.url}
          />
          {emitted.revoked && (
            <p className="cell-sub drawer__note">
              El enlace del {words.assigneeLower} anterior quedó revocado.
            </p>
          )}
        </>
      ) : (
        <ProviderAssignForm
          observation={{
            id: obs.id,
            severity: obs.severity,
            providerId: obs.providerId,
            assetTitle: storeName,
          }}
          notifiesAssetParty={notifiesAssetParty}
          onAssigned={onAssigned}
          onCancel={onClose}
        />
      )}
    </Drawer>
  );
}
